/**
 * Real User Monitoring (RUM) — Core Web Vitals tracking.
 *
 * Capture les métriques de performance perçues par les utilisateurs réels :
 *   - LCP (Largest Contentful Paint) — temps avant le contenu principal
 *   - INP (Interaction to Next Paint) — réactivité au clic
 *   - CLS (Cumulative Layout Shift) — stabilité visuelle
 *   - FCP (First Contentful Paint) — premier rendu
 *   - TTFB (Time To First Byte) — temps réseau serveur
 *
 * Envoie les métriques au backend via /api/metrics/web-vitals (batch, fire-and-forget).
 * Pas d'impact perceptible sur l'UX (sendBeacon avec fallback fetch).
 */
import { onCLS, onINP, onLCP, onFCP, onTTFB } from 'web-vitals';

const ENDPOINT = `${process.env.REACT_APP_BACKEND_URL || ''}/api/metrics/web-vitals`;
const BUFFER = [];
let flushTimer = null;

function flush() {
  if (BUFFER.length === 0) return;
  const batch = BUFFER.splice(0, BUFFER.length);
  const body = JSON.stringify({
    metrics: batch,
    user_agent: navigator.userAgent.slice(0, 200),
    url: window.location.pathname,
    timestamp: Date.now(),
  });

  // sendBeacon = fire-and-forget, ne bloque pas la navigation/fermeture onglet
  if (navigator.sendBeacon) {
    try {
      navigator.sendBeacon(ENDPOINT, new Blob([body], { type: 'application/json' }));
      return;
    } catch (e) {
      // fallback fetch
    }
  }
  try {
    fetch(ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body,
      credentials: 'include',
      keepalive: true,
    }).catch(() => {});
  } catch (e) {
    // silent: RUM ne doit jamais casser l'UX
  }
}

function track(metric) {
  BUFFER.push({
    name: metric.name,
    value: Math.round(metric.value * 100) / 100,
    rating: metric.rating,
    id: metric.id,
    delta: metric.delta,
  });
  // Debounce flush : envoie batch après 2s d'inactivité
  if (flushTimer) clearTimeout(flushTimer);
  flushTimer = setTimeout(flush, 2000);
}

export function initWebVitals() {
  try {
    onLCP(track);
    onINP(track);
    onCLS(track);
    onFCP(track);
    onTTFB(track);
    // Flush au unload pour ne rien perdre
    window.addEventListener('pagehide', flush);
    window.addEventListener('beforeunload', flush);
  } catch (e) {
    // RUM ne doit jamais bloquer l'app
    if (process.env.NODE_ENV === 'development') {
      // eslint-disable-next-line no-console
      console.warn('[web-vitals] init failed', e);
    }
  }
}
